import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, output, ViewEncapsulation, input, effect, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AuthenticationRequest, Policy } from '@ap/fido-uaf-frontend-client';
import { ApAuthenticationService } from '@ap/browser-authenticator';
import { ApContentService } from '@ap/content';
import { TokenTypeId, ApAuthenticatieFidoResource, CommunicationType } from '@ap/interface-apic-authenticatie-fido';
import { TaskStatus } from '@ap/interface-apic-authenticatiebeheer';
import { ApButtonComponent } from '@ap/button';
import { ApContentComponent } from '@ap/content';
import { ApNotificationComponent } from '@ap/notification';
import { ApContentPipe } from '@ap/content';

import { PendingToken, SignCredentials, SignError, SignToken } from './sign.model';
import { TokenType } from './token/selector/selector.model';
import { ApTokenSelectorService } from './token/selector/selector.service';
import { ApTokenService } from './token/token.service';
import { ValidationResult, ValidationResultType } from './validation/validation.model';
import { ApValidationResultService } from './validation/validation.service';
import { ApBrowserService } from './token/browser/browser.service';
import content from './sign.content.json';
import { ApSignSkeletonComponent } from './skeleton/skeleton.component';
import { ApWysiwysComponent } from './wysiwys/wysiwys.component';
import { ApTokenSelectorSkeletonComponent } from './token/selector/skeleton/skeleton.component';
import { ApTokenSelectorComponent } from './token/selector/selector.component';
import { ApDigicodeSkeletonComponent } from './token/digicode/skeleton/skeleton.component';
import { ApDigicodeComponent } from './token/digicode/digicode.component';
import { ApDigipasSkeletonComponent } from './token/digipas/skeleton/skeleton.component';
import { ApDigipasComponent } from './token/digipas/digipas.component';
import { ApNativeAppSkeletonComponent } from './token/native-app/skeleton/skeleton.component';
import { ApNativeAppComponent } from './token/native-app/native-app.component';
import { ApBrowserSkeletonComponent } from './token/browser/skeleton/skeleton.component';
import { ApBrowserComponent } from './token/browser/browser.component';
import { ApPhoneNumberSkeletonComponent } from './token/phone-number/skeleton/skeleton.component';
import { ApSignPhoneNumberComponent } from './token/phone-number/phone-number.component';
import { ApSignErrorPipe } from './error/error.pipe';

/**
 * @module ApSignModule
 * @ngdoc component
 * @name ApSignComponent
 */
@Component({
    selector: 'ap-sign',
    standalone: true,
    templateUrl: 'sign.component.html',
    styleUrls: ['./sign.component.scss'],
    providers: [ApTokenSelectorService],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ApSignSkeletonComponent,
        ApWysiwysComponent,
        ApButtonComponent,
        ApContentComponent,
        ApNotificationComponent,
        ApTokenSelectorSkeletonComponent,
        ApTokenSelectorComponent,
        ApDigicodeSkeletonComponent,
        ApDigicodeComponent,
        ApDigipasSkeletonComponent,
        ApDigipasComponent,
        ApNativeAppSkeletonComponent,
        ApNativeAppComponent,
        ApBrowserSkeletonComponent,
        ApBrowserComponent,
        ApPhoneNumberSkeletonComponent,
        ApSignPhoneNumberComponent,
        ApContentPipe,
        ApSignErrorPipe
    ]
})
export class ApSignComponent implements OnInit {
    private readonly authenticationResource = inject(ApAuthenticatieFidoResource);
    private readonly authenticationService = inject(ApAuthenticationService);
    private readonly contentService = inject(ApContentService);
    private readonly tokenService = inject(ApTokenService);
    private readonly validationResultService = inject(ApValidationResultService);

    /**
     * Required - The actionId that identifies the action to be signed
     */
    public readonly actionId = input.required<string>();

    /**
     * Required - Text to show on submit button
     */
    public readonly submitText = input.required<string>();

    /**
     * Button to register a new browser
     */
    public readonly browserRegister = input<boolean>(undefined);

    /**
     * Text to show on cancel button
     */
    public readonly cancelText = input<string>(undefined);

    /**
     * Text that goes on the secondary cancel button, if omitted the button won't show
     */
    public readonly secondaryCancelText = input<string>(undefined);

    /**
     * Should Wysiwys component be shown?
     * Defaults to true
     */
    public readonly showWysiwys = input(true);

    /**
     * Initialize with a preselected token
     */
    public readonly initWithToken = input<SignToken>(undefined);

    /**
     * Optional formcontrol on which the selected token will be saved.
     */
    public readonly rememberControl = input<FormControl<boolean>>(null);

    /**
     * Can a selected token be deselected?
     * Defaults to true
     */
    public readonly allowDeselect = input(true);

    /**
     * Passed to `ap-browser` component.
     * Show button if user has forgotten credentials.
     * Defaults to false
     */
    public readonly showLostCredentials = input(false);

    /**
     * URL Origin for cors requests
     */
    public readonly corsOrigin = input<string>(undefined);

    /**
     * Are pending tokens allowed? Used when signing with BROWSER.
     * Defaults to false
     */
    public readonly allowPendingTokens = input(false);

    /**
     * Array of additional allowed tokens next to default tokens.
     * Defauklt tokens are: `SignToken.DIGIPAS`, `SignToken.DIGICODE`, `SignToken.BROWSER`, `SignToken.NATIVE_APP`, `SignToken.PHONE_NUMBER`
     */
    public readonly additionalTokens = input<SignToken[]>([]);

    /**
     * Id of key to save token by
     */
    public readonly saveTokenIdKey = input(null);

    /**
     * Is signing implicit?
     * The header for selecting from a multi-user browser registration will be hidden when set to `true`
     * Defaults to false
     */
    public readonly implicitSigning = input(false);

    /**
     * Id's of elements that describe token inputs for accessibility.
     */
    public readonly ariaDescribedByIds = input<string[]>([]);

    /**
     * Emits when sign is canceled
     */
    public canceled = output<void>();

    /**
     * Emits event when user finishes sign process
     */
    public done = output<void>();

    /**
     * Emits event when initialization of sign is finished
     */
    public initDone = output<void>();

    /**
     * Emits event when a retry of signing is triggered
     */
    public retry = output<void>();

    /**
     * Emits when sign is canceled
     */
    public secondaryCanceled = output<void>();

    /**
     * Emits when selected token has changed
     */
    public tokenChanged = output<SignToken>();
    public updatePassword = output<SignCredentials>();
    public updateUsernamePassword = output<SignCredentials>();
    public signedWithPendingToken = output<PendingToken>();
    public browserInvalidCredentials = output<string>();
    public browserLostCredentials = output<string>();
    public signError = output<string>();

    public authenticationRequest: AuthenticationRequest;
    public initialStatus: TaskStatus;
    public error: SignError;
    public keyId: string;
    public tokenTypes: TokenType[];
    public readonly signToken = SignToken;
    public selectedToken: SignToken;
    public hasAuthenticatedToken: boolean;
    public tokenToSelect: SignToken;

    protected browserCodeError: string;

    private selectedPolicy: SignToken[];
    private lastSelectedToken: SignToken;
    private isFinished: boolean;

    private readonly defaultTokens = [
        SignToken.DIGIPAS,
        SignToken.DIGICODE,
        SignToken.BROWSER,
        SignToken.NATIVE_APP,
        SignToken.PHONE_NUMBER
    ];

    public constructor() {
        effect(() => {
            if (this.actionId()) {
                this.initialize(this.actionId());
            }
        });
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
    }

    public isAnonymous(): boolean {
        return this.tokenService.isAnonymous(this.authenticationRequest.policy);
    }

    public isTokenSelectorSelected(): boolean {
        return !this.selectedToken && !this.tokenToSelect;
    }

    public isSelected(signToken: SignToken): boolean {
        return this.selectedToken === this.tokenToSelect && this.selectedToken === signToken;
    }

    public isLoading(signToken: SignToken): boolean {
        return this.selectedToken !== this.tokenToSelect && this.tokenToSelect === signToken && !this.error;
    }

    public onCancel(): void {
        this.isFinished = true;
        this.canceled.emit();
    }

    public async onDone(): Promise<void> {
        delete this.error;
        // the keyId is used to re-initialize after a failed attempt
        // when signing succeeded this key should be removed
        // to avoid the next token of step-up to use this keyId
        delete this.keyId;
        if (this.isStepUp()) {
            this.initialize(this.actionId());
        } else {
            this.isFinished = true;
            this.done.emit();
        }
    }

    public onError(error: SignError): void {
        this.error = error;

        if (this.error && !this.error.key) {
            this.error.key = 'unknown_error';
        }

        if (this.error?.key) {
            this.signError.emit(this.error.key);
        }
    }

    public onRetry(keyId?: string): void {
        this.keyId = keyId;
        this.tokenToSelect = this.selectedToken;
        this.selectedToken = null;
        this.isFinished = true;
        this.retry.emit();
    }

    public onRestart(keyId: string): void {
        this.keyId = keyId;
        this.tokenToSelect = this.selectedToken;
        this.selectedToken = null;
        this.initialize(this.actionId());
    }

    public onTokenCanceled(): void {
        this.secondaryCanceled.emit();
    }

    public canDeselectToken(): boolean {
        return (
            this.allowDeselect() &&
            !this.isFinished &&
            !!this.selectedToken &&
            this.selectedToken === this.tokenToSelect &&
            this.tokenTypes.length > 1
        );
    }

    public getFirstTokenToSelect(signTokens: SignToken[]): SignToken {
        if (signTokens && signTokens.length > 1) {
            const hasPhone = this.tokenService.getDecodedKeyIds(this.authenticationRequest.policy, TokenTypeId.PHONE_NUMBER);
            if (this.tokenService.isAnonymous(this.authenticationRequest.policy) && hasPhone && !hasPhone[0]) {
                return signTokens.filter((token: SignToken) => token !== SignToken.PHONE_NUMBER)[0];
            }
            return signTokens[0];
        }
        return signTokens && signTokens[0];
    }

    public updateToken(signTokens: SignToken[]): void {
        this.tokenToSelect = this.getFirstTokenToSelect(signTokens);
        this.selectedPolicy = signTokens;
        delete this.keyId;
        delete this.error;
        if (this.tokenToSelect !== null && this.lastSelectedToken === SignToken.NATIVE_APP) {
            // When the native app picks up the sign action, its session is transfered to that device.
            // When the user selects another token in the browser, we need to transfer the session
            // back to us. This is done by re-retrieving the authenticationRequest which transfers
            // the session to us.
            this.initialize(this.actionId());
        } else {
            this.tokenService
                .getAuthenticationStatus(this.actionId())
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        const validationResult = this.mapToValidationResult(error);
                        this.signalConsumers(validationResult.resultType);
                        throw error;
                    })
                )
                .subscribe((status: TaskStatus) => {
                    if (status === TaskStatus.UNAUTHENTICATED_EXPIRED || status === TaskStatus.PARTIALLY_AUTHENTICATED_EXPIRED) {
                        this.signalConsumers(ValidationResultType.PROCESS_RETRY);
                    } else if (status === TaskStatus.CANCELLED) {
                        this.signalConsumers(ValidationResultType.DONE);
                    } else if (status === TaskStatus.AUTHENTICATED) {
                        this.signalConsumers(ValidationResultType.DONE);
                    } else {
                        this.commitToken(this.tokenToSelect);
                    }
                });
        }
    }

    public onUpdatePassword(signCredentials: SignCredentials): void {
        this.isFinished = true;
        this.updatePassword.emit(signCredentials);
    }

    public onUpdateUsernamePassword(signCredentials: SignCredentials): void {
        this.isFinished = true;
        this.updateUsernamePassword.emit(signCredentials);
    }

    public onSignedWithPendingToken(token: PendingToken): void {
        this.isFinished = true;
        this.signedWithPendingToken.emit(token);
    }

    public onBrowserInvalidCredentials(keychainId: string): void {
        this.browserInvalidCredentials.emit(keychainId);
    }

    public onBrowserLostCredentials(keychainId: string): void {
        this.browserLostCredentials.emit(keychainId);
    }

    private commitToken(signToken: SignToken): void {
        this.selectedToken = signToken;
        this.tokenChanged.emit(signToken);
        if (signToken !== null) {
            this.lastSelectedToken = signToken;
        }
    }

    private signalConsumers(status: ValidationResultType): void {
        switch (status) {
            case ValidationResultType.DONE:
                this.done.emit();
                break;
            case ValidationResultType.PROCESS_RETRY:
                this.retry.emit();
                break;
        }
    }

    private mapToValidationResult(httpError: HttpErrorResponse): ValidationResult {
        const reason = httpError.error && httpError.error.code;
        const anonymous = this.tokenService.isAnonymous(this.authenticationRequest.policy);
        return this.validationResultService.getValidationResult(reason, anonymous);
    }

    private getTokenToSelect(tokens: SignToken[][]): SignToken {
        // when retrying a signing attempt the same token is preselected
        if (
            this.tokenToSelect !== undefined &&
            (this.tokenToSelect === null || this.findPolicyContaining(tokens, this.tokenToSelect) !== -1)
        ) {
            // value can be null as well, which is explicitely empty
            return this.tokenToSelect;
        }

        // if step 1 of step-up failed, we re-select this token,
        // otherwise we move on to step 2 of step-up
        // The second token is the tokens that has a match with selectedPolicy
        if (this.isStepUp()) {
            if (this.stepUpFailed(tokens)) {
                return this.getFirstTokenToSelect(this.selectedPolicy);
            }

            return this.selectedPolicy.filter((token: SignToken) => tokens.flat().indexOf(token) !== -1)[0];
        }

        // the proces might specify a specific token to open with
        // opening with PHONE_NUMBER is never allowed
        const initWithToken = this.initWithToken();
        if (initWithToken && initWithToken !== SignToken.PHONE_NUMBER) {
            // Only allow a token in a step-up to be selected when anonymous
            // We don't want to autoselect a step-up token for a logged in user because
            // the user should be given a chance to use a different non step-up token
            const index = this.isAnonymous()
                ? this.findPolicyContaining(tokens, initWithToken)
                : this.findPolicyMatching(tokens, [initWithToken]);
            if (index !== -1) {
                this.selectedPolicy = tokens[index];
                return initWithToken;
            }
        }

        // the token that is used for log in is also selected for signing
        // unless the token is part of a step-up
        const authenticatedToken = this.tokenService.getAuthenticatedToken(this.authenticationRequest.policy);
        if (authenticatedToken && this.findPolicyMatching(tokens, [authenticatedToken]) !== -1) {
            return authenticatedToken;
        }

        // otherwise no token is preselected
        return null;
    }

    private initialize(actionId: string): void {
        const authentication$ = this.authenticationResource.getOneAuthenticationRequest(actionId);
        const status$ = this.tokenService.getAuthenticationStatus(actionId);

        this.authenticationService.isBrowserCompatible().subscribe((compatible) => {
            const observables = compatible ? [authentication$, this.initCors()] : [authentication$];
            status$
                .pipe(
                    map((status: TaskStatus) => {
                        if (status === TaskStatus.AUTHENTICATED) {
                            this.isFinished = true;
                            this.done.emit();
                            return throwError(() => ({
                                error: {
                                    code: 'ALREADY_SIGNED'
                                }
                            }));
                        }
                        return of(status);
                    }),
                    switchMap((status: Observable<TaskStatus>) => forkJoin([status, ...observables]))
                )
                .subscribe({
                    next: ([status, startRequest]: [TaskStatus, AuthenticationRequest]) =>
                        this.setupState(startRequest, status, compatible),
                    error: (err) => {
                        if (err.error.code === 'ALREADY_SIGNED') {
                            return;
                        }

                        this.onError({ key: err.error.code, type: 'error' });
                        this.initDone.emit();
                    }
                });
        });
    }

    private initCors(): Observable<void> {
        const corsOrigin = this.corsOrigin();
        return corsOrigin ? this.authenticationService.initCors(corsOrigin, '/online/sign-cors/index.html', 2000) : of(null);
    }

    private setupState(authenticationRequest: AuthenticationRequest, initialStatus: TaskStatus, compatible: boolean): void {
        const policy = authenticationRequest.policy;

        this.isFinished = false;

        this.authenticationRequest = authenticationRequest;
        this.initialStatus = initialStatus;

        const tokens = this.tokenService.getAcceptedTokens(policy);
        const browserPolicyIndex = this.findPolicyContaining(tokens, SignToken.BROWSER);
        const disabledBrowser = browserPolicyIndex !== -1 && !compatible;

        this.getAcceptedAndAvailableTokens(policy).subscribe((acceptedTokens: SignToken[][]) => {
            if (acceptedTokens.length > 0 || disabledBrowser) {
                this.tokenTypes = ApTokenSelectorService.createSelector(acceptedTokens);
                this.tokenTypes = this.changePhoneSelector(this.tokenTypes);
                if (disabledBrowser) {
                    const browser = ApTokenSelectorService.createSelector([tokens[browserPolicyIndex]])[0];
                    this.tokenTypes.push({
                        ...browser,
                        disabled: true,
                        helpText: this.contentService.get('sign.browser.disabled')
                    });
                } else if (
                    this.isAnonymous() &&
                    this.findPolicyContaining(acceptedTokens, SignToken.BROWSER) === -1 &&
                    browserPolicyIndex > -1 &&
                    this.browserRegister()
                ) {
                    const browser = ApTokenSelectorService.getBrowserRegisterSelector();
                    this.tokenTypes.push({
                        ...browser
                    });
                }
                this.tokenToSelect = this.getTokenToSelect(acceptedTokens);

                //exit setup because tokens has already been signed.
                if (initialStatus === TaskStatus.AUTHENTICATED && this.tokenToSelect === this.lastSelectedToken) {
                    this.isFinished = true;
                    // TODO: The 'emit' function requires a mandatory void argument
                    this.done.emit();
                    return;
                }

                this.hasAuthenticatedToken = this.hasAcceptedAuthenticatedToken(acceptedTokens, policy);
                if (this.isStepUp() && !this.stepUpFailed(acceptedTokens)) {
                    delete this.selectedPolicy;
                }
                this.commitToken(this.tokenToSelect);
            } else {
                this.onError(
                    this.tokenService.isFinancialTransaction(authenticationRequest.transaction)
                        ? { key: 'no_available_tokens.financial', type: 'error' }
                        : { key: 'no_available_tokens', type: 'error' }
                );
            }
            // TODO: The 'emit' function requires a mandatory void argument
            this.initDone.emit();
        });
    }

    private hasAcceptedAuthenticatedToken(acceptedTokens: SignToken[][], policy: Policy): boolean {
        const authenticatedToken = this.tokenService.getAuthenticatedToken(policy);
        return !!authenticatedToken && this.findPolicyMatching(acceptedTokens, [authenticatedToken]) >= 0;
    }

    private getAcceptedAndAvailableTokens(policy: Policy): Observable<SignToken[][]> {
        let acceptedTokens = [...this.tokenService.getAcceptedTokens(policy)];
        const allowedTokens = [...this.defaultTokens, ...this.additionalTokens()];
        acceptedTokens = acceptedTokens.filter((tokens) => !tokens.find((token) => allowedTokens.indexOf(token) === -1));
        let acceptedKeychainIds: Observable<string[]>;

        if (this.isAnonymous()) {
            acceptedKeychainIds = this.authenticationService.getTokenIds();
        } else {
            const acceptedBrowserKeyIds = this.tokenService.getKeyIds(policy, TokenTypeId.BROWSER_DEVICE);
            const acceptedPincodeKeyIds = this.tokenService.getKeyIds(policy, TokenTypeId.BROWSER_PINCODE);
            acceptedKeychainIds = this.authenticationService.getMatchingKeychainIds(acceptedBrowserKeyIds, acceptedPincodeKeyIds);
        }
        return acceptedKeychainIds.pipe(
            map((keychainIds: string[]) => {
                this.browserCodeError = '';
                keychainIds.forEach((key) => {
                    this.authenticationService.getToken(key).subscribe((authToken) => {
                        this.browserCodeError += `${this.browserCodeError ? '-' : ''}${authToken.stage.substring(0, 1) + authToken.keychainId.slice(authToken.keychainId.length - 5)}`;
                    });
                });

                return keychainIds;
            }),
            ApBrowserService.filterPendingTokens(this.allowPendingTokens(), this.authenticationService),
            map((keychainIds: string[]) => {
                //remove browser selector if we don't have one
                const browserIndex = this.findPolicyContaining(acceptedTokens, SignToken.BROWSER);
                if (keychainIds.length === 0 && browserIndex >= 0) {
                    this.browserCodeError += `#`;
                    acceptedTokens.splice(browserIndex, 1);
                }
                //remove phone selector when we don't have phonenumber
                if (this.tokenService.checkPolicyForMissingPhonenumber(this.authenticationRequest.policy)) {
                    const phoneIndex = this.findPolicyContaining(acceptedTokens, SignToken.PHONE_NUMBER);
                    if (acceptedTokens.length === 1) {
                        acceptedTokens.splice(phoneIndex, 1);
                    }
                }
                return acceptedTokens;
            })
        );
    }

    private findPolicyContaining(policy: SignToken[][], token: SignToken): number {
        return policy.findIndex((tokens) => tokens.indexOf(token) !== -1);
    }

    private findPolicyMatching(policy: SignToken[][], tokens: SignToken[]): number {
        return policy.findIndex((policyTokens) => this.isEqualPolicy(policyTokens, tokens));
    }

    private isEqualPolicy(policy1: SignToken[], policy2: SignToken[]): boolean {
        return policy1.length === policy2.length && !policy1.find((token) => policy2.indexOf(token) === -1);
    }

    private isStepUp(): boolean {
        return this.selectedPolicy && this.selectedPolicy.length > 1;
    }

    private changePhoneSelector(tokenTypes: TokenType[]): TokenType[] {
        let keyIds = this.tokenService.getDecodedKeyIds(this.authenticationRequest.policy, TokenTypeId.PHONE_NUMBER);

        //remove phone selector when we don't have phonenumber
        if (this.tokenService.checkPolicyForMissingPhonenumber(this.authenticationRequest.policy)) {
            return tokenTypes.filter((e) => e.ids.indexOf(SignToken.PHONE_NUMBER) === -1);
        }

        if (keyIds == null || keyIds.length === 0) {
            return tokenTypes;
        }
        let phoneNumberMetadata = this.tokenService.getPhoneNumberMetadata(this.authenticationRequest.policy);
        let isLandLine = false;
        if (phoneNumberMetadata != null) {
            isLandLine = phoneNumberMetadata.communicationType === CommunicationType.VOICE;
        } else {
            isLandLine = !keyIds[0].startsWith('00316');
        }

        if (isLandLine) {
            for (const token of tokenTypes) {
                token.name = token.name.replace('phone', 'landline');
            }
        }
        return tokenTypes;
    }

    private stepUpFailed(tokens: SignToken[][]): boolean {
        // step-up failed when the tokens from the current policy
        // are present in the original policy
        return this.findPolicyMatching(tokens, this.selectedPolicy) !== -1;
    }
}
